import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiPost, apiGet } from '../utils/apiAuth';
import config from '../config';

const popupStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1050,
    backdropFilter: 'blur(2px)',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px 16px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 24px',
    minHeight: '200px',
    maxHeight: '400px',
  },
  commentItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    background: '#f8fafc',
    borderRadius: '8px',
    borderLeft: '3px solid #e5e7eb',
  },
  resolveCommentItem: {
    background: '#f0fdf4',
    borderLeftColor: '#22c55e',
  },
  commentText: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#374151',
    marginBottom: '8px',
    wordBreak: 'break-word',
  },
  commentMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
    color: '#6b7280',
  },
  author: {
    fontWeight: 500,
    color: '#4b5563',
  },
  timeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
  },
  time: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#6b7280',
    lineHeight: 1.2,
  },
  date: {
    fontSize: '11px',
    color: '#9ca3af',
    lineHeight: 1.2,
  },
  footer: {
    padding: '16px 24px 20px',
    borderTop: '1px solid #e5e7eb',
    background: '#f9fafb',
  },
  textarea: {
    flex: 1,
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    resize: 'vertical',
    minHeight: '40px',
    maxHeight: '100px',
  },
  submitButton: {
    borderRadius: '8px',
    minWidth: '100px',
  },
  resolvedCount: {
    fontSize: '12px',
    color: '#dc2626',
    fontWeight: 500,
    background: '#fef2f2',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #fecaca',
  },
};

const CommentPopup = ({ 
  isOpen, 
  onClose, 
  onRefresh,
  referenceType, 
  referenceId, 
  userEmail, 
  userName 
}) => {
  // Debug: Log the received props
  useEffect(() => {
  }, [userEmail, userName]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [isResolved, setIsResolved] = useState(true);
  const [error, setError] = useState('');
  
  const commentsEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new comments are added
  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Focus input when popup opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiGet(
        `${config.API_BASE_URL}/api/comments/?reference_type=${referenceType}&reference_id=${referenceId}&comment_type=general`
      );
      
      if (response.status === 200) {
        setComments(response.data.comments || []);
        setResolvedCount(response.data.resolved_count || 0);
        setIsResolved(response.data.resolved_count === 0);
      } else {
        setError('Failed to load comments');
      }
    } catch (error) {
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [referenceType, referenceId]);

  // Load comments when popup opens
  useEffect(() => {
    if (isOpen && referenceType && referenceId && referenceId !== 'null' && referenceId !== 'undefined') {
      loadComments();
    }
  }, [isOpen, referenceType, referenceId, loadComments]);

  // Scroll to bottom when comments change
  useEffect(() => {
    scrollToBottom();
  }, [comments]);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    
    // Validate required fields
    if (!referenceType || !referenceId || referenceId === 'null' || referenceId === 'undefined' || referenceId === null || referenceId === undefined) {
      setError('Invalid reference data. Please try again.');
      return;
    }
    
    if (!userEmail || !userName) {
      setError('User information not available. Please refresh the page.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      
      const payload = {
        reference_type: referenceType,
        reference_id: referenceId,
        comment_type: 'general',
        comment_text: newComment.trim(),
        commented_by: userEmail,
        commented_by_name: userName,
        attachments: null
      };
      
      const response = await apiPost(`${config.API_BASE_URL}/api/comments/`, payload);
      
      if (response.status === 200 || response.status === 201) {
        setNewComment('');
        // Reload comments to get updated list and resolved count
        await loadComments();
        // Trigger refresh of the parent component to update comment counts
        if (onRefresh) {
          onRefresh();
        }
      } else {
        setError('Failed to post comment');
      }
    } catch (error) {
      setError('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async () => {
    try {
      setSubmitting(true);
      setError('');
      
      const payload = {
        reference_type: referenceType,
        reference_id: referenceId,
        resolved_by: userEmail,
        resolved_by_name: userName
      };
      
      const response = await apiPost(`${config.API_BASE_URL}/api/comments/resolve`, payload);
      
      if (response.status === 200) {
        // Reload comments to get updated list and resolved count
        await loadComments();
        // Trigger refresh of the parent component to update comment counts
        if (onRefresh) {
          onRefresh();
        }
      } else {
        setError('Failed to resolve comments');
      }
    } catch (error) {
      setError('Failed to resolve comments');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    
    // Convert to IST (UTC+5:30)
    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    
    // Format time in 12-hour format
    const timeOptions = { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    };
    
    // Format date
    const dateOptions = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      timeZone: 'Asia/Kolkata'
    };
    
    const timeStr = istDate.toLocaleTimeString('en-IN', timeOptions);
    const dateStr = istDate.toLocaleDateString('en-IN', dateOptions);
    
    return (
      <div style={popupStyles.timeContainer}>
        <div style={popupStyles.time}>{timeStr}</div>
        <div style={popupStyles.date}>{dateStr}</div>
      </div>
    );
  };

  const isResolveComment = (comment) => {
    return comment.comment_text.includes('✅ Resolved by');
  };

  if (!isOpen) return null;

  return (
    <div style={popupStyles.overlay} onClick={onClose}>
      <div style={popupStyles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={popupStyles.header}>
          <h3 style={popupStyles.title}>Comments</h3>
          <button style={popupStyles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        {/* Comments List */}
        <div style={popupStyles.body}>
          {loading ? (
            <div className="d-flex flex-column align-items-center text-muted py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="mt-2">Loading comments...</span>
            </div>
          ) : error ? (
            <div className="text-center py-3">
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
              <button onClick={loadComments} className="btn btn-primary btn-sm">
                Retry
              </button>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center text-muted fst-italic py-4">
              No comments yet. Be the first to comment!
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {comments.map((comment, index) => {
                const resolveStyle = isResolveComment(comment)
                  ? popupStyles.resolveCommentItem
                  : {};
                return (
                  <div
                    key={comment.id || index}
                    style={{ ...popupStyles.commentItem, ...resolveStyle }}
                  >
                    <div style={popupStyles.commentText}>{comment.comment_text}</div>
                    <div style={popupStyles.commentMeta}>
                      <span style={popupStyles.author}>{comment.commented_by_name}</span>
                      <span style={popupStyles.timeContainer}>{formatTime(comment.commented_at)}</span>
                    </div>
                  </div>
                );
              })}
              <div ref={commentsEndRef} />
            </div>
          )}
        </div>

        {/* Input Section */}
        <div style={popupStyles.footer}>
          <div className="d-flex flex-column flex-md-row gap-3 mb-3">
            <textarea
              ref={inputRef}
              style={popupStyles.textarea}
              className="form-control"
              placeholder="Type your comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={2}
              disabled={submitting}
            />
            <button
              type="button"
              className="btn btn-primary"
              style={popupStyles.submitButton}
              onClick={handleSubmitComment}
              disabled={!newComment.trim() || submitting}
            >
              {submitting ? 'Posting...' : 'Comment'}
            </button>
          </div>

          {/* Resolve Button */}
          <div className="d-flex flex-column flex-md-row gap-3 align-items-center justify-content-between">
            <button
              type="button"
              className={`btn ${isResolved ? 'btn-light' : 'btn-danger'}`}
              onClick={handleResolve}
              disabled={isResolved || submitting || comments.length === 0}
            >
              {isResolved ? 'Resolved' : 'Resolve'}
            </button>
            {resolvedCount > 0 && (
              <span style={popupStyles.resolvedCount}>
                {resolvedCount} unresolved
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentPopup;

